// The languages this build ships a translation for, and the one every
// screen falls back to. `preferences.language` (account's own contract)
// allows a wider set than this (S6.04/S6.05, Decided in spec: two languages
// ship now; a third account value such as "pt" is a real, accepted choice
// that simply has no catalogue yet, so every key it reads falls back to
// English per key, rather than refusing the value or collapsing it to
// "en" in storage).

export const DEFAULT_LANGUAGE = "en";
export const SUPPORTED_LANGUAGES = ["en", "es"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Any value `preferences.language` may hold, wider than what has a catalogue. */
export type Language = string;

export const LANGUAGE_STORAGE_KEY = "amtana-language";

/** The language a stored string resolves to; an empty store (nobody has
 * chosen yet) reads as `fallback`: the default, or what the device's country suggests (S10.08). */
export function resolveLanguage(stored: string | null, fallback: Language = DEFAULT_LANGUAGE): Language {
  return stored && stored.length > 0 ? stored : fallback;
}

// The website's own address for Spanish (ADR-027 point 5): English lives at the root, Spanish
// under this one prefix. The platform's routes never start with it, so the platform's stored
// preference (above) keeps deciding there.
const SPANISH_PATH_PREFIX = "/es";

/** The language a website URL's "/es/" prefix names; `undefined` for the English root (which
 * the platform's routes share, so its stored preference is never shadowed). */
export function languageFromPath(pathname: string): Language | undefined {
  return pathname === SPANISH_PATH_PREFIX || pathname.startsWith(`${SPANISH_PATH_PREFIX}/`)
    ? "es"
    : undefined;
}

/** The same path with its "/es" prefix removed, for matching against a screen's own path. */
export function pathWithoutLanguage(pathname: string): string {
  if (pathname === SPANISH_PATH_PREFIX) return "/";
  if (pathname.startsWith(`${SPANISH_PATH_PREFIX}/`)) return pathname.slice(SPANISH_PATH_PREFIX.length);
  return pathname;
}

/** A path with no language prefix, at the language's own address: itself for English, under
 * "/es" for Spanish. */
export function pathWithLanguage(pathname: string, language: SupportedLanguage): string {
  if (language !== "es") return pathname;
  return pathname === "/" ? SPANISH_PATH_PREFIX : `${SPANISH_PATH_PREFIX}${pathname}`;
}

// Whether the URL is the one deciding the language at all (ADR-027 point 5): set once, by
// `@amtana/browser`'s `renderPage` and `hydrate`, the only callers that render a website's own
// prerendered page. Off, as it is for the platform (ADR-027 point 5, S6.04, S10.08), a stored
// choice is answered and an empty store still falls back to the device's own country (S10.08).
// On, `active-language.ts`'s `read()` never falls back to the device: the URL already answered,
// and a browser whose own languages differ gets a one-line offer instead (D964), never a
// silent switch.
let urlDecidesLanguage = false;

/** Call once, before the first render that must read the active language: turns off the
 * device-locale fallback everywhere, because the URL answers instead. */
export function enableUrlLanguage(): void {
  urlDecidesLanguage = true;
}

export function urlLanguageEnabled(): boolean {
  return urlDecidesLanguage;
}
